import { NgZone } from '@angular/core';
import { takeWhile } from 'rxjs/operators';
import { ICancellationToken } from 'src/app/common/contracts/cancellation/cancellation-token';
import { toError } from 'src/app/common/logging/error-normalization';

import { ISyncService } from '../contracts/sync/service/sync-service';
import { IWebSyncService } from '../contracts/sync/service/web-sync-service';

export class SyncTimeoutRepeater {
  private _runCount = 0;

  private _delaySchedule = [
    0,
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 5, // After 5 seconds
    1000 * 10, // After 10 seconds
    1000 * 20, // After 20 seconds
  ];

  constructor(
    private _syncService: ISyncService | IWebSyncService,
    private _ngZone: NgZone,
    private _userSync?: any
  ) {}

  public async sync(token: ICancellationToken, user): Promise<any> {
    let timedOut = false;
    let serviceName;

    do {
      await this.sleep(this._delaySchedule[this._runCount++], token);
      const { timedOut: timedOut1, serviceName: serviceName1 } = await this.run(token, user);
      timedOut = timedOut1;
      serviceName = serviceName || serviceName1;
    } while (timedOut);

    if (serviceName && token.serviceRepeats.hasOwnProperty(serviceName)) {
      delete token.serviceRepeats[serviceName];
    }

    // Service settled (completed, errored-and-exhausted, ignored or cancelled): mark it terminal.
    this.markTerminal(token);
  }

  /**
   * Marks this service as terminal so the sync indicator can decide when every service is done.
   * Done eagerly on error too (not only after retries exhaust): an unreachable service keeps
   * retrying for minutes, and the indicator must not hang waiting for it (work item 15660). The
   * background retries continue regardless - closing the indicator does not stop the sync.
   */
  private markTerminal(token: ICancellationToken): void {
    const serviceName = (this._syncService as { name?: string }).name;
    if (!serviceName) {
      return;
    }
    token.syncTerminal = token.syncTerminal || {};
    token.syncTerminal[serviceName] = true;
  }

  private async run(token: ICancellationToken, user): Promise<any> {
    try {
      await this._syncService.sync(token, user, this.resetServiceRepeats);
      if (this.isSyncService(this._syncService)) {
        await this._syncService.reSync(token, user, this._userSync, this.resetServiceRepeats);
      }
    } catch (error) {
      this.markTerminal(token);
      if (!error) {
        // Reading `.data` off a null/undefined rejection used to crash this handler and
        // reject the whole repeater run — poisoning the cancellation token it runs on
        // (work item 27022). Log it here, with the failing service's name.
        const serviceName = (this._syncService as { name?: string }).name ?? 'unknown service';
        window.logger.error(
          `Sync of ${serviceName} failed`,
          toError(error, `Sync of ${serviceName} failed`)
        );
        return { timedOut: false, serviceName };
      }
      if (!error.data || !error.data.path) {
        return { timedOut: false, serviceName: error.service };
      }

      token.serviceRepeats[error.data.path] = token.serviceRepeats.hasOwnProperty(error.data.path)
        ? token.serviceRepeats[error.data.path] + 1
        : 0;

      if (
        error.code &&
        (error.code === 408 || error.code === 404) &&
        !token.cancelled.get() &&
        token.serviceRepeats[error.data.path] <= this._delaySchedule.length - 1
      ) {
        return { timedOut: true, serviceName: error.data.path };
      }

      if (token.serviceRepeats.hasOwnProperty(error.data.path)) {
        delete token.serviceRepeats[error.data.path];
        return { timedOut: false, serviceName: error.data.path };
      }
    }

    return { timedOut: false };
  }

  private resetServiceRepeats(token: ICancellationToken, serviceName: string) {
    if (token.serviceRepeats.hasOwnProperty(serviceName)) {
      token.serviceRepeats[serviceName] = 0;
    }
  }

  private isSyncService(service: any): service is ISyncService {
    return service && typeof service.reSync === 'function';
  }

  private sleep(milliseconds: number, token: ICancellationToken) {
    return new Promise<void>(resolve => {
      token.cancelled.data$.pipe(takeWhile(cancelled => !cancelled)).subscribe({ complete: () => resolve() });
      this._ngZone.runOutsideAngular(() => setTimeout(resolve, milliseconds || 0));
    });
  }
}
